import { useI18n } from "../../lib/i18n.jsx";
import { useTrips } from "../../lib/useTrips.js";
import { useReviews } from "../../lib/useReviews.js";
import { useCountdown } from "../../lib/hooks.js";
import { formatPrice } from "../../lib/format.js";
import { Star, Users, Shield } from "lucide-react";
import { useMemo } from "react";
import LazyHeroVideo from "../LazyHeroVideo.jsx";

export default function Hero() {
  const { t, lang } = useI18n();
  const { trips, loading } = useTrips();
  const { live, rating, ratingCount } = useReviews();

  // Next upcoming + cheapest available trip = source of truth for hero stats
  const next = useMemo(() => {
    const now = Date.now();
    const upcoming = trips.filter(
    x => !x.is_disabled && new Date(`${x.date_start}T15:00:00+02:00`).getTime() > now
    );
    if (!upcoming.length) return null;
    return upcoming.slice().sort((a, b) => a.date_start.localeCompare(b.date_start))[0];
  }, [trips]);

  const cheapestPrice = useMemo(() => {
    const list = trips.filter(x => x.available).map(x => x.promo_price ?? x.price);
    return list.length ? Math.min(...list) : null;
  }, [trips]);

  const countdownIso = next ? `${next.date_start}T15:00:00+02:00` : null;
  const c = useCountdown(countdownIso);
  const nextLabel = next
    ? (() => {
        const seasons = {
          pl: { summer: "Lato", winter: "Zima" },
          uk: { summer: "Літо", winter: "Зима" },
          en: { summer: "Summer", winter: "Winter" },
        };
        const dict = seasons[lang] || seasons.en;
        return `${dict[next.season]} ${next.date_start.slice(0, 4)}`;
      })()
    : t.hero.next_label;
  const hasCountdown = !!next;

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-cream overflow-hidden"
    >
      <div className="absolute top-32 right-0 w-72 h-72 bg-summer/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-64 left-0 w-96 h-96 bg-sun/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 hero-rise" style={{ animationDelay: "60ms" }}>

            <h1
              data-testid="hero-title"
              className="font-display font-bold text-winter leading-[1] text-balance"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
            >
              {t.hero.title_1}{" "}
              <span className="font-handwritten text-summer text-[1.15em]">
                {t.hero.title_2}
              </span>
              <br />
              {t.hero.title_3}
            </h1>

            <p className="mt-6 text-lg md:text-xl text-winter/70 leading-relaxed max-w-xl">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/lato" data-testid="hero-cta-summer" className="btn-pill btn-pill-primary">
                {t.hero.cta_summer} →
              </a>
              <a href="/zima" data-testid="hero-cta-winter" className="btn-pill btn-pill-winter">
                {t.hero.cta_winter} →
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-winter/65">
              <a
                href="#opinie"
                data-testid="hero-rating-link"
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("opinie")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <div className="flex">
                  {[...Array(5)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-sun text-sun" />))}
                </div>
                <span className="font-semibold">
                  {live ? Number(rating).toFixed(1) : "5.0"} · Google
                  {live && ratingCount != null && (
                    <span className="text-winter/55 font-normal"> ({ratingCount})</span>
                  )}
                </span>
              </a>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-summer" />
                <span className="font-semibold">{t.hero.trust_safety}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-summer" />
                <span className="font-semibold">{t.hero.trust_kids}</span>
              </div>
            </div>
          </div>

          {/* Hero media – auto-playing summer video with poster fallback */}
          <div className="lg:col-span-5 relative hero-rise" style={{ animationDelay: "200ms" }}>
            <LazyHeroVideo
              className="aspect-[4/5] rounded-3xl overflow-hidden bg-paper"
              poster="/img/hero-summer-poster.jpg"
              sources={[
                { src: "/videos/summer-720.mp4", media: "(max-width: 768px)" },
                { src: "/videos/summer-1280.mp4" },
              ]}
              data-testid="hero-video"
            >
              {cheapestPrice != null && (
                <div className="absolute -top-4 -right-4 bg-sun text-winter rounded-full w-24 h-24 flex flex-col items-center justify-center transform rotate-12 shadow-xl">
                  <span className="font-handwritten text-xl leading-none">
                    {{ pl: "od", uk: "від", en: "from" }[lang] || "from"}
                  </span>
                  <span className="font-display font-bold text-xs uppercase tracking-wider mt-1">{formatPrice(cheapestPrice)}</span>
                </div>
              )}
            </LazyHeroVideo>
          </div>
        </div>

        {/* Countdown */}
        <div
          data-testid="hero-countdown"
          className="mt-14 md:mt-20 bg-white rounded-3xl border border-winter/10 p-5 md:p-8 hero-rise"
          style={{ animationDelay: "340ms" }}
        >
          {loading ? (
            <div className="flex flex-col md:flex-row md:items-center md:gap-8 animate-pulse">
              <div className="text-center md:text-left mb-5 md:mb-0 md:flex-shrink-0">
                <div className="h-3 w-24 bg-winter/10 rounded mb-2 mx-auto md:mx-0" />
                <div className="h-7 w-36 bg-winter/10 rounded mx-auto md:mx-0" />
              </div>
              <div className="flex-1 grid grid-cols-4 gap-2 sm:gap-4 md:border-l md:border-winter/10 md:pl-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="text-center">
                    <div className="h-10 w-14 bg-winter/10 rounded mx-auto mb-2" />
                    <div className="h-2 w-10 bg-winter/10 rounded mx-auto" />
                  </div>
                ))}
              </div>
            </div>
          ) : hasCountdown ? (
            <div className="flex flex-col md:flex-row md:items-center md:gap-8">
              <div className="text-center md:text-left mb-5 md:mb-0 md:flex-shrink-0">
                 <p className="font-display uppercase text-xl md:text-2xl font-bold text-winter">{nextLabel}</p>
                <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-winter/50 mb-1">
                  {t.hero.countdown}
                </p>
              </div>
              <div className="flex-1 grid grid-cols-4 gap-2 sm:gap-4 md:border-l md:border-winter/10 md:pl-8">
                {[
                  [c.days, t.hero.days],
                  [c.hours, t.hero.hours],
                  [c.minutes, t.hero.minutes],
                  [c.seconds, t.hero.seconds]
                ].map(([val, label], i) => (
                  <div key={i} className="text-center">
                    <p
                      className="font-display font-bold text-winter leading-none tabular-nums"
                      style={{ fontSize: "clamp(1.75rem, 5vw, 3rem)" }}
                    >
                      {String(val).padStart(2, "0")}
                    </p>
                    <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] text-winter/50 mt-1">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-testid="hero-countdown-empty">
              <div className="text-center md:text-left">
                <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-summer mb-1.5">
                  {t.hero.no_upcoming_kicker}
                </p>
                <p className="font-display text-xl md:text-2xl font-bold text-winter leading-tight">
                  {t.hero.no_upcoming_title}
                </p>
                <p className="text-sm text-winter/60 mt-1">
                  {t.hero.no_upcoming_body}
                </p>
              </div>
              <a
                href="#newsletter"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid="hero-no-upcoming-cta"
                className="btn-pill btn-pill-primary self-center md:self-auto whitespace-nowrap"
              >
                {t.hero.no_upcoming_cta} →
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
